// VRP Unified Provider - VRP-Specific Context Providers Only
// QueryClientProvider and ThemeProvider are provided by App.tsx to avoid duplication
import React, { ReactNode } from 'react';

// Core VRP Providers (Original imports for reference)
import { AuthProvider } from './AuthContext';
import { VRPDataProvider } from './VRPDataContext';
import { VRPUIProvider } from './VRPUIContext';
import { VRPNotificationProvider } from './VRPNotificationContext';

// Error Handling
import { VRPErrorBoundaryProvider } from './VRPErrorBoundaryProvider';

// Context Readiness Management
import { ContextReadinessProvider, ReadinessDisplay } from './ContextReadinessManager';

// Readiness-Aware Provider Wrappers
import { ReadinessAwareProviders, ReadinessDebugInfo } from './ReadinessAwareProviders';

interface VRPUnifiedProviderProps {
  children: ReactNode;
  enableReadinessDebug?: boolean;
}

export const VRPUnifiedProvider: React.FC<VRPUnifiedProviderProps> = ({
  children,
  enableReadinessDebug = process.env.NODE_ENV === 'development'
}) => {
  // Log to verify single provider instance
  React.useEffect(() => {
    console.log('✅ [VRPUnifiedProvider] Mounted - Using App.tsx QueryClient and Theme providers with Context Readiness Management');
  }, []);

  return (
    <ContextReadinessProvider
      enableDebugLogging={enableReadinessDebug}
      callbacks={{
        onEssentialReady: () => {
          console.log('🎯 [VRPUnifiedProvider] Essential contexts (Auth, Theme, UI) are ready');
        },
        onCoreReady: () => {
          console.log('🎯 [VRPUnifiedProvider] Core contexts (Essential + Data) are ready');
        },
        onCompleteReady: () => {
          console.log('🎯 [VRPUnifiedProvider] All contexts ready - VRP system fully initialized');
        }
      }}
    >
      <ReadinessAwareProviders.ErrorBoundary
        level="application"
        enableRecovery={false}
        fallback={(error, retry) => (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h3>Application Error</h3>
            <p>Something went wrong: {error.message}</p>
            <button onClick={retry}>Try Again</button>
          </div>
        )}
      >
        <ReadinessAwareProviders.Auth>
          <ReadinessAwareProviders.Data>
            <ReadinessAwareProviders.Project>
              <ReadinessAwareProviders.Scenario>
                <ReadinessAwareProviders.UI>
                  <ReadinessAwareProviders.Notifications>
                    <ReadinessAwareProviders.NavigationTracker>
                      {children}
                      {/* Debug components for development */}
                      {enableReadinessDebug && (
                        <>
                          <ReadinessDebugInfo />
                          <ReadinessDisplay compact />
                        </>
                      )}
                    </ReadinessAwareProviders.NavigationTracker>
                  </ReadinessAwareProviders.Notifications>
                </ReadinessAwareProviders.UI>
              </ReadinessAwareProviders.Scenario>
            </ReadinessAwareProviders.Project>
          </ReadinessAwareProviders.Data>
        </ReadinessAwareProviders.Auth>
      </ReadinessAwareProviders.ErrorBoundary>
    </ContextReadinessProvider>
  );
};

// For testing purposes - Test provider should be set up at test level with proper providers
export const VRPUnifiedTestProvider: React.FC<{
  children: ReactNode;
  mockData?: Record<string, any>;
}> = ({ children }) => {
  // For tests, the test setup should provide QueryClient, Theme, and Router
  // This component only provides VRP-specific contexts
  return (
    <VRPErrorBoundaryProvider level="application">
      <AuthProvider>
        <VRPDataProvider>
          <VRPUIProvider>
            <VRPNotificationProvider>
              {children}
            </VRPNotificationProvider>
          </VRPUIProvider>
        </VRPDataProvider>
      </AuthProvider>
    </VRPErrorBoundaryProvider>
  );
};

// Performance monitoring hook
export const useVRPPerformance = () => {
  const [metrics, setMetrics] = React.useState({
    renderCount: 0,
    lastRenderTime: 0,
    averageRenderTime: 0
  });

  React.useEffect(() => {
    const startTime = performance.now();
    
    setMetrics(prev => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      const newRenderCount = prev.renderCount + 1;
      const newAverageRenderTime = (prev.averageRenderTime * prev.renderCount + renderTime) / newRenderCount;
      
      return {
        renderCount: newRenderCount,
        lastRenderTime: renderTime,
        averageRenderTime: newAverageRenderTime
      };
    });
  });

  return metrics;
};

// Enhanced provider status hook using ContextReadinessManager
export const useVRPProviderStatus = () => {
  try {
    const { useContextReadiness } = require('./ContextReadinessManager');
    const { state, getInitializationProgress, getNotReadyContexts } = useContextReadiness();
    
    return {
      // Individual context status
      auth: state.auth,
      data: state.data,
      ui: state.ui,
      theme: state.theme,
      navigation: state.navigation,
      notifications: state.notifications,
      errorBoundary: state.errorBoundary,
      
      // Computed readiness levels
      essential: state.essential,
      core: state.core,
      complete: state.complete,
      
      // Additional metrics
      progress: getInitializationProgress(),
      notReady: getNotReadyContexts(),
      
      // Legacy compatibility
      isReady: state.essential // Backward compatibility
    };
  } catch (error) {
    // Fallback for when ContextReadinessManager is not available
    console.warn('[VRPUnifiedProvider] ContextReadinessManager not available, using fallback status');
    return {
      auth: true,
      data: true,
      ui: true,
      theme: true,
      navigation: true,
      notifications: true,
      errorBoundary: true,
      essential: true,
      core: true,
      complete: true,
      progress: { ready: 7, total: 7, percentage: 100 },
      notReady: [],
      isReady: true
    };
  }
};